import * as React from 'react';
import type { TreeItem } from 'react-complex-tree';
import { ControlledTreeEnvironment, InteractionMode, Tree } from 'react-complex-tree';
import { cn } from '../../../utils';
import { Input } from '../../atoms';
import { ComplexTreeEmptyState } from './components/empty-state';
import { createComplexTreeRenderItem } from './components/item-render';
import { ComplexTreeLoadingState } from './components/loading-state';
import { useComplexTreeHandlers } from './hooks/use-complex-tree-handlers';
import { useComplexTreeSearch } from './hooks/use-complex-tree-search';
import { useTreeItemsAdapter } from './hooks/use-tree-items-adapter';
import type { IComplexTreeProps, ITreeNode } from './types';

const DEFAULT_MAX_DEPTH = 10;
const DEFAULT_MAX_CHILDREN_PER_NODE = 75;

//TODO: change these inputs to allow more component overrides for how the tree renders https://bond-sports.atlassian.net/browse/BOND-15043
export function ComplexTree({
	treeData,
	selectedNodeIds,
	setSelectedNodeIds,
	expandedNodeIds,
	setExpandedNodeIds,
	maxDepth = DEFAULT_MAX_DEPTH,
	maxChildrenPerNode = DEFAULT_MAX_CHILDREN_PER_NODE,
	isVisible = true,
	onVisibilityChange,
	isLoading = false,
	loadingStateIcon,
	emptyStateIcon,
	labels,
	className = '',
	treeId = 'complex-tree',
	treeItemMoreOptions,
}: IComplexTreeProps) {
	const safeExpandedNodeIds = expandedNodeIds ?? new Set();
	const safeSelectedNodeIds = selectedNodeIds ?? new Set();

	const treeItems = useTreeItemsAdapter(treeData, maxDepth, maxChildrenPerNode);
	const { onSelectItems, onExpandItem, onCollapseItem, isTreeEmpty, focusedItem, onFocusItem } = useComplexTreeHandlers(
		treeItems,
		setSelectedNodeIds,
		safeExpandedNodeIds,
		setExpandedNodeIds
	);
	const { searchTerm, debouncedSearchTerm, handleSearch, doesItemMatchSearch } = useComplexTreeSearch(
		treeItems,
		setExpandedNodeIds
	);

	React.useEffect(() => {
		onVisibilityChange?.(isVisible);
	}, [isVisible, onVisibilityChange]);

	const renderItem = React.useMemo(() => {
		return createComplexTreeRenderItem({
			doesItemMatchSearch,
			searchTerm: debouncedSearchTerm,
			maxDepth,
			maxChildrenPerNode,
			moreOptions: treeItemMoreOptions,
			truncatedText: labels?.truncatedText,
			depthLimitedText: labels?.depthLimitedText,
			ariaLabels: labels?.ariaLabels,
		});
	}, [
		maxDepth,
		maxChildrenPerNode,
		doesItemMatchSearch,
		debouncedSearchTerm,
		treeItemMoreOptions,
		labels?.truncatedText,
		labels?.depthLimitedText,
		labels?.ariaLabels,
	]);

	const getItemTitle = React.useCallback((item: TreeItem<ITreeNode>) => item.data?.label || '', []);

	if (!isVisible) {
		return null;
	}

	const ariaLabels = labels?.ariaLabels;

	return (
		<div
			className={cn(
				`flex h-full w-120 shrink-0 flex-col rounded-lg border border-borderSeparator bg-white p-[2rem]`,
				className
			)}
			data-testid="complex-tree"
			role="region"
			aria-label={ariaLabels?.treeContainer}
		>
			{/* TODO: implement shared search component https://bond-sports.atlassian.net/browse/BOND-15041*/}
			<Input
				variant="search"
				placeholder={labels?.searchPlaceholder}
				value={searchTerm}
				onChange={handleSearch}
				data-testid="complex-tree-search-input"
				width="100%"
				aria-label={ariaLabels?.searchInput}
			/>
			<div
				className={cn(
					'mt-[1.2rem] border-t border-borderSeparator pt-[1.2rem]',
					isLoading || isTreeEmpty() ? 'flex h-full flex-col' : 'min-h-0 overflow-y-auto'
				)}
				data-testid="complex-tree-items-list"
				role="region"
				aria-label={ariaLabels?.treeList}
				aria-busy={isLoading}
			>
				{isLoading ? (
					<ComplexTreeLoadingState
						message={labels?.loadingStateMessage}
						icon={loadingStateIcon}
						ariaLabel={ariaLabels?.loadingState}
					/>
				) : isTreeEmpty() ? (
					<ComplexTreeEmptyState
						message={labels?.emptyStateMessage}
						icon={emptyStateIcon}
						ariaLabel={ariaLabels?.emptyState}
					/>
				) : (
					<ControlledTreeEnvironment
						items={treeItems}
						defaultInteractionMode={InteractionMode.ClickArrowToExpand}
						getItemTitle={getItemTitle}
						viewState={{
							[treeId]: {
								expandedItems: Array.from(safeExpandedNodeIds),
								selectedItems: Array.from(safeSelectedNodeIds),
								focusedItem,
							},
						}}
						onSelectItems={onSelectItems}
						onExpandItem={onExpandItem}
						onFocusItem={onFocusItem}
						onCollapseItem={onCollapseItem}
						renderItem={renderItem}
						canSearch={false}
						canSearchByStartingTyping={false}
						data-testid="complex-tree-controlled-environment"
					>
						<Tree treeId={treeId} rootItem="root" treeLabel={ariaLabels?.tree} data-testid="complex-tree-tree" />
					</ControlledTreeEnvironment>
				)}
			</div>
		</div>
	);
}
